import axios from "axios";
import type { SolutionFeedback, DesktopFeedback } from "../types/feedback/feedback";
import { getAccessToken } from "../lib/auth-token";
import { getPreviewToken } from "../lib/preview-session";

const API_URL = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const previewToken = getPreviewToken();
  if (previewToken) {
    config.headers['x-preview-token'] = previewToken;
    return config;
  }
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const CACHE_DURATION = 5000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheStore {
  userRecords: { [key: string]: CacheEntry<any> };
  progress: { [key: string]: CacheEntry<any> };
}

const cache: CacheStore = {
  userRecords: {},
  progress: {},
};

const isCacheValid = (entry: CacheEntry<any>) => {
  return Date.now() - entry.timestamp < CACHE_DURATION;
};

const isValidCacheKey = (key: string): boolean => {
  return typeof key === "string" && key.length > 0 && /^[a-zA-Z0-9_-]+$/.test(key);
};

interface GeneralFeedback {
  userId: string;
  input_type: string;
  context_area: string;
  description: string;
  evaluating_vendors: string;
  vendor_names: string;
  urgency_level: string;
  follow_up: string;
}

export const checkTenantAndUser = async (
  tenantId: string,
  userId: string,
  name: string,
  email: string,
  companyName: string
) => {
  try {
    const response = await api.post('/api/customers', {
      frontegg_tenant: tenantId,
      frontegg_accountname: companyName,
      frontegg_clientname: name,
      frontegg_userid: userId,
      frontegg_email: email,
    });
    return response.data.message;
  } catch (error) {
    console.error("Error communicating with database:", error);
    throw error;
  }
};

const getUserRecord = async (userId: string) => {
  try {
    if (isValidCacheKey(userId)) {
      const cachedRecord = cache.userRecords[userId];
      if (cachedRecord && isCacheValid(cachedRecord)) {
        return cachedRecord.data;
      }
    }

    const response = await api.get(`/api/customers/${userId}`);
    
    if (isValidCacheKey(userId)) {
      cache.userRecords[userId] = {
        data: response.data,
        timestamp: Date.now(),
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching user record:", error);
    throw error;
  }
};

export const updateProgress = async (
  userId: string,
  type: "solutions" | "desktop",
  progress: number
) => {
  try {
    await api.patch(`/api/progress/${userId}`, { type, progress });
    
    if (isValidCacheKey(userId)) {
      delete cache.userRecords[userId];
      delete cache.progress[userId];
    }
    
    return "Progress updated successfully";
  } catch (error) {
    console.error("Error updating progress:", error);
    throw error;
  }
};

export const getProgress = async (
  userId: string
): Promise<{
  solutions: number;
  desktop: number;
  solutionsChecks: string[];
  desktopChecks: string[];
}> => {
  try {
    if (isValidCacheKey(userId)) {
      const cachedProgress = cache.progress[userId];
      if (cachedProgress && isCacheValid(cachedProgress)) {
        return cachedProgress.data;
      }
    }

    const response = await api.get(`/api/progress/${userId}`);
    
    if (isValidCacheKey(userId)) {
      cache.progress[userId] = {
        data: response.data,
        timestamp: Date.now(),
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching progress:", error);
    return {
      solutions: 0,
      desktop: 0,
      solutionsChecks: [],
      desktopChecks: [],
    };
  }
};

export const updateChecks = async (
  userId: string,
  type: "solutions" | "desktop",
  checks: string[]
) => {
  try {
    await api.patch(`/api/checks/${userId}`, { type, checks });
    
    if (isValidCacheKey(userId)) {
      delete cache.userRecords[userId];
      delete cache.progress[userId];
    }
    
    return "Checks updated successfully";
  } catch (error) {
    console.error("Error updating checks:", error);
    throw error;
  }
};

export const submitFeedback = async ({
  userId,
  input_type,
  context_area,
  description,
  evaluating_vendors,
  vendor_names,
  urgency_level,
  follow_up,
}: GeneralFeedback) => {
  try {
    const customer = await getUserRecord(userId);
    const customerEmail = customer.frontegg_email;

    if (!customerEmail) {
      throw new Error("Customer email not found");
    }

    await api.post('/api/feedback', {
      client: customerEmail,
      input_type,
      context_area,
      description,
      evaluating_vendors,
      vendor_names,
      urgency_level,
      follow_up,
    });

    return "Feedback submitted successfully";
  } catch (error) {
    console.error("General feedback submission error:", error);
    throw error;
  }
};

export const submitSolutionFeedback = async (feedback: SolutionFeedback) => {
  try {
    const customer = await getUserRecord(feedback.clientId);
    const customerEmail = customer.frontegg_email;

    if (!customerEmail) {
      throw new Error("Customer email not found");
    }

    await api.post('/api/solutions-feedback', {
      client: customerEmail,
      solution: feedback.solutionName,
      rating: feedback.rating,
      impact_response: feedback.impactResponse || '',
      challenge_text: feedback.challengeText || '',
      priority_level: feedback.priorityLevel || '',
      buying_signal: feedback.buyingSignal || '',
      role_relation: feedback.roleRelation || '',
      relative_importance: feedback.relativeImportance || '',
      why_not: feedback.whyNot || '',
    });

    return "Solution feedback submitted successfully";
  } catch (error) {
    console.error("Solution feedback submission error:", error);
    throw error;
  }
};

export const submitDesktopFeedback = async (
  userId: string,
  feedback: DesktopFeedback
) => {
  try {
    const customer = await getUserRecord(userId);
    const customerEmail = customer.frontegg_email;
    const customerName = customer.frontegg_accountname;

    if (!customerEmail) {
      throw new Error("Customer email not found");
    }
    if (!customerName) {
      throw new Error("Customer name not found");
    }

    await api.post('/api/desktop-feedback', {
      client: customerName,
      email: customerEmail,
      usefulness: feedback.usefulness,
      troubleshoot: feedback.troubleshoot,
      applications: feedback.applications,
      knowledge: feedback.knowledge,
      automation: feedback.automation,
      status_page: feedback.status_page,
      migrations: feedback.migrations,
      bots: feedback.bots,
      ticketing: feedback.ticketing,
      account_access: feedback.account_access,
      device_health: feedback.device_health,
      security_policies: feedback.security_policies,
      contacts_assistance: feedback.contacts_assistance,
      challenges: feedback.challenges,
      urgency: feedback.urgency,
      exploration_stage: feedback.exploration_stage,
      adoption_likelihood: feedback.adoption_likelihood,
      follow_up_interest: feedback.follow_up_interest,
      additional: feedback.additional,
    });

    return "Desktop feedback submitted successfully";
  } catch (error) {
    console.error("Desktop feedback submission error:", error);
    throw error;
  }
};

export const getIntegrationFeedback = async (email: string) => {
  try {
    const response = await api.get(`/api/integrations-feedback/${encodeURIComponent(email)}`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching integration feedback:", error);
    throw error;
  }
};

export const submitIntegrationFeedback = async (
  email: string,
  integrations: { name: string; description: string }[]
) => {
  try {
    await api.post('/api/integrations-feedback', {
      client: email,
      integrations,
    });
    return "Integration feedback submitted successfully";
  } catch (error) {
    console.error("Error submitting integration feedback:", error);
    throw error;
  }
};

export const submitInvestmentFeedback = async (
  email: string,
  type: string,
  range: string,
  advisory: boolean
) => {
  try {
    await api.post('/api/investments', {
      email,
      type,
      range,
      advisory,
    });
    return "Investment feedback submitted successfully";
  } catch (error) {
    console.error("Investment feedback submission error:", error);
    throw error;
  }
};

export const submitSweepstakesAcceptance = async (email: string) => {
  try {
    await api.post('/api/sweepstakes', {
      client: email,
      terms: "Yes",
    });
    return "Sweepstakes acceptance recorded successfully";
  } catch (error) {
    console.error("Error submitting sweepstakes acceptance:", error);
    throw error;
  }
};
